import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const { t } = useTranslation();

  // Charger la langue depuis localStorage au démarrage
  useEffect(() => {
    const savedLang = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(savedLang);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
      setCartCount(total);
    };
    
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || !isHome ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/3604b3d12fd82860bb853f77e2d33e20.jpeg" 
              alt="P'tit Voyou" 
              className="h-11 w-11 md:h-14 md:w-14 object-contain rounded-full bg-white p-1 shadow-sm"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`transition-colors text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer ${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`}
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/boutique" 
              className={`transition-colors text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer ${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`}
            >
              {t('nav.shop')}
            </Link>
            <Link 
              to="/contact" 
              className={`transition-colors text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer ${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`}
            >
              {t('nav.contact')}
            </Link>
          </div>

          {/* Language Selector, Cart & Mobile Menu */}
          <div className="flex items-center space-x-3 md:space-x-4">
            {/* Language Selector - Desktop */}
            <div className="hidden md:flex items-center space-x-2">
              <button
                onClick={() => changeLanguage('en')}
                className={`text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
                  i18n.language === 'en' ? 'text-brand-pink border-b-2 border-brand-pink' : `${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`
                }`}
              >
                ENG
              </button>
              <span className={`text-sm ${isScrolled || !isHome ? 'text-gray-400' : 'text-white'}`}>/</span>
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
                  i18n.language === 'fr' ? 'text-brand-pink border-b-2 border-brand-pink' : `${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`
                }`}
              >
                FR
              </button>
            </div>

            <Link 
              to="/panier" 
              className={`relative transition-colors cursor-pointer p-1 ${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-brand-pink'}`}
            >
              <i className="ri-shopping-cart-2-line text-2xl"></i>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-pink text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden transition-colors cursor-pointer p-1 ${isScrolled || !isHome ? 'text-gray-800 hover:text-brand-pink' : 'text-white hover:text-gray-300'}`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-2">
            <Link 
              to="/" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center py-4 text-gray-800 hover:text-brand-pink transition-colors text-base font-bold tracking-wide cursor-pointer border-b border-gray-100"
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/boutique" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center py-4 text-gray-800 hover:text-brand-pink transition-colors text-base font-bold tracking-wide cursor-pointer border-b border-gray-100"
            >
              {t('nav.shop')}
            </Link>
            <Link 
              to="/contact" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center py-4 text-gray-800 hover:text-brand-pink transition-colors text-base font-bold tracking-wide cursor-pointer border-b border-gray-100"
            >
              {t('nav.contact')}
            </Link>
            
            {/* Language Selector - Mobile */}
            <div className="flex items-center space-x-4 py-4">
              <button
                onClick={() => { changeLanguage('en'); setIsMobileMenuOpen(false); }}
                className={`text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors px-3 py-1 rounded-full ${
                  i18n.language === 'en' ? 'bg-brand-pink text-white' : 'text-gray-600 border border-gray-300'
                }`}
              >
                ENG
              </button>
              <button
                onClick={() => { changeLanguage('fr'); setIsMobileMenuOpen(false); }}
                className={`text-sm font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors px-3 py-1 rounded-full ${
                  i18n.language === 'fr' ? 'bg-brand-pink text-white' : 'text-gray-600 border border-gray-300'
                }`}
              >
                FR
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}